import React, { FC, ReactElement, ReactNode, useMemo } from 'react';

import CMSPlaceholderViewElement from 'editors/FormulaViewer/components/CMSPlaceholderViewElement';
import styled from 'styled-components';

import aampeTheme from '../../../../constants/aampeTheme';
import ICONS from '../../../../constants/icons';

import { ComponentTypeTransformed } from 'models/component-types-model';
import { DatasetModel } from 'models/dataset.model';
import {
    ElementKind,
    SectionElement,
    VariantElement,
} from 'models/editor.model';
import { VariantType } from 'models/variant.model';

import DatasetContent from './DatasetContent';
import VariantContent from './VariantContent';

interface MapElementContentProps {
    element: SectionElement;
    componentTypes?: ComponentTypeTransformed;
    datasetData: DatasetModel;
}

const MapElementContent: FC<MapElementContentProps> = ({
    element,
    componentTypes,
    datasetData,
}) => {
    const content = useMemo<ReactElement>(() => {
        const parts: ReactNode[] = [];
        element.children.forEach((child, index) => {
            if ((element as any).variantType == VariantType.Image) {
                parts.push(
                    <VariantContent
                        componentTypes={componentTypes}
                        child={element as any}
                    />,
                );
            } else if ('text' in child && child.text) {
                parts.push(
                    <span
                        key={`text-${index}`}
                        style={{ display: 'inline', verticalAlign: 'middle' }}
                    >
                        {child.text}
                    </span>,
                );
            } else if ('kind' in child && child.kind == ElementKind.variant) {
                parts.push(
                    <VariantContent
                        componentTypes={componentTypes}
                        child={child as VariantElement}
                    />,
                );
            } else if ('kind' in child && child.kind == ElementKind.dataset) {
                parts.push(
                    <DatasetContent child={child} datasetData={datasetData} />,
                );
            } else if (
                'kind' in child &&
                child.kind == ElementKind.attributePlaceholder
            ) {
                parts.push(
                    <PlaceholderComponent>
                        <StyledIcon />
                        {child.defaultValue}
                    </PlaceholderComponent>,
                );
            } else if (
                'kind' in child &&
                child.kind == ElementKind.cms_placeholder
            ) {
                parts.push(<StyledCMSPlaceholderViewElement element={child} />);
            }
        });

        return (
            <span
                style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    minHeight: '15px',
                }}
            >
                {parts}
            </span>
        );
    }, [element, componentTypes, datasetData]);

    return <StructureElementSection>{content}</StructureElementSection>;
};

const StructureElementSection = styled.div({
    display: 'flex',
    borderRadius: aampeTheme.borderRadius.default,
    border: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    padding: aampeTheme.spacing4(3),
    fontSize: aampeTheme.size.font.textMedium,
    fontWeight: aampeTheme.weights.light,
});

const PlaceholderComponent = styled.span({
    userSelect: 'none',
    outline: 'none',
    display: 'inline-block',
    borderRadius: aampeTheme.borderRadius.default,
    backgroundColor: aampeTheme.colors.interactiveWG01,
    padding: aampeTheme.spacing4(0, 1),
    color: aampeTheme.colors.neutral09,
    lineHeight: aampeTheme.size.lineHeight.default,
    fontSize: aampeTheme.size.font.textMedium,
    fontWeight: aampeTheme.weights.light,
    minWidth: '30px',
    border: `1px solid ${aampeTheme.colors.solidA02}`,
    cursor: 'pointer',
});

const StyledIcon = styled(ICONS.attributePlaceholder)({
    margin: aampeTheme.spacing4(0, 1, 1, 0),
    verticalAlign: 'middle',
    width: '16px',
    height: '16px',
    color: 'inherit',
});

const StyledCMSPlaceholderViewElement = styled(CMSPlaceholderViewElement)({
    fontSize: aampeTheme.size.font.textMedium,
});
export default MapElementContent;
